import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import * as authService from '../services/authService';
import useIdleTimeout from '../hooks/useIdleTimeout';
import { getToken, SESSION_EXPIRED_EVENT } from '../services/apiClient';
import { connectSocket, disconnectSocket } from '../services/messagingService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Optimistic: render with the cached user while the token is validated.
  const [user, setUser] = useState(() => authService.loadUser());
  const [loading, setLoading] = useState(() => !!getToken());

  useEffect(() => {
    if (!getToken()) return;
    let active = true;
    authService.fetchMe()
      .then((me) => { if (active) setUser(me); })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Axios cannot update React state directly. Keep the visible session in
  // sync when an authenticated request proves the JWT is no longer valid.
  useEffect(() => {
    const expireSession = () => {
      disconnectSocket();
      setUser(null);
      setLoading(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expireSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expireSession);
  }, []);

  // Keep the realtime socket in lockstep with the session.
  useEffect(() => {
    if (user) connectSocket();
    else disconnectSocket();
  }, [user]);

  const signIn = useCallback(async (identifier, password) => {
    const loggedIn = await authService.login(identifier, password);
    setUser(loggedIn);
    return loggedIn;
  }, []);

  const signUp = useCallback(async ({ name, email, password, role, phone, termsAccepted }) => {
    const newUser = await authService.signUp({ name, email, password, role, phone, termsAccepted });
    setUser(newUser);
    return newUser;
  }, []);

  const signInWithGoogle = useCallback(async (credential, role) => {
    const loggedIn = await authService.signInWithGoogle(credential, role);
    setUser(loggedIn);
    return loggedIn;
  }, []);

  const signOut = useCallback(() => {
    authService.signOut();
    disconnectSocket();
    setUser(null);
  }, []);

  // Security: auto-logout after 30 minutes of inactivity (parity with main).
  useIdleTimeout(signOut, 30, !!user);

  const refresh = useCallback(async () => {
    const me = await authService.fetchMe();
    setUser(me);
    return me;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      authService.cacheUser(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signInWithGoogle, signUp, signOut, refresh, updateUser }),
    [user, loading, signIn, signInWithGoogle, signUp, signOut, refresh, updateUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
